import { Paper, Stack, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { DateTime } from 'luxon';
import { type FC, useMemo } from 'react';

import { type RouterNetworkDay, type RouterNetworkSimSummary } from '@okvc/schema';

import {
  availabilityRows,
  OFFLINE_SERIES_KEY,
  simLabel,
  UNKNOWN_SERIES_KEY,
} from './routerNetworkFormat.js';

const CHART_HEIGHT = 260;
const BYTES_PER_MEGABYTE = 1_048_576;

interface RouterNetworkChartsProps {
  days: RouterNetworkDay[];
  sims: RouterNetworkSimSummary[];
}

const hours = (value: number | null) => (value === null ? '' : `${value} h`);

const RouterNetworkCharts: FC<RouterNetworkChartsProps> = ({ days, sims }) => {
  const theme = useTheme();
  const availability = useMemo(() => availabilityRows(days), [days]);
  const streamHealth = useMemo(
    () => days.map((day) => ({
      label: DateTime.fromISO(day.day).toFormat('LLL d'),
      downtime: day.streamDowntimeMinutes,
      uploaded: Math.round(day.uploadedBytes / BYTES_PER_MEGABYTE),
    })),
    [days],
  );

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1">Time on each SIM per day</Typography>
        <BarChart
          height={CHART_HEIGHT}
          dataset={availability}
          xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
          series={[
            ...sims.map((sim) => ({ dataKey: sim.id, label: simLabel(sim), stack: 'day', valueFormatter: hours })),
            {
              dataKey: OFFLINE_SERIES_KEY,
              label: 'Offline',
              stack: 'day',
              color: theme.palette.error.main,
              valueFormatter: hours,
            },
            {
              dataKey: UNKNOWN_SERIES_KEY,
              label: 'Not monitored',
              stack: 'day',
              color: theme.palette.grey[400],
              valueFormatter: hours,
            },
          ]}
        />
      </Paper>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1">Video lost per day (minutes)</Typography>
          <BarChart
            height={CHART_HEIGHT}
            dataset={streamHealth}
            xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
            series={[{ dataKey: 'downtime', label: 'Stream downtime', color: theme.palette.warning.main }]}
          />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1">Video uploaded per day (MB)</Typography>
          <BarChart
            height={CHART_HEIGHT}
            dataset={streamHealth}
            xAxis={[{ scaleType: 'band', dataKey: 'label' }]}
            series={[{ dataKey: 'uploaded', label: 'Uploaded' }]}
          />
        </Paper>
      </Stack>
    </Stack>
  );
};

export default RouterNetworkCharts;
